
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import {
  KeyenceHardKey,
  keyenceHardKeyEventManager,
  HardKeyHandler,
} from '@/utils/keyenceHT/keyenceHardKeyEventHandler'

// Key icon components
import Key0 from '@/components/svg/keyenceHT/Key0.vue'
import Key1 from '@/components/svg/keyenceHT/Key1.vue'
import Key2 from '@/components/svg/keyenceHT/Key2.vue'
import Key3 from '@/components/svg/keyenceHT/Key3.vue'
import Key4 from '@/components/svg/keyenceHT/Key4.vue'
import Key5 from '@/components/svg/keyenceHT/Key5.vue'
import Key6 from '@/components/svg/keyenceHT/Key6.vue'
import Key7 from '@/components/svg/keyenceHT/Key7.vue'
import Key8 from '@/components/svg/keyenceHT/Key8.vue'
import Key9 from '@/components/svg/keyenceHT/Key9.vue'
import KeyC from '@/components/svg/keyenceHT/KeyC.vue'
import KeyEnt from '@/components/svg/keyenceHT/KeyEnt.vue'
import KeyF1 from '@/components/svg/keyenceHT/KeyF1.vue'
import KeyF2 from '@/components/svg/keyenceHT/KeyF2.vue'
import KeyF3 from '@/components/svg/keyenceHT/KeyF3.vue'
import KeyF4 from '@/components/svg/keyenceHT/KeyF4.vue'
import KeyTab from '@/components/svg/keyenceHT/KeyTab.vue'
import KeyPeriod from '@/components/svg/keyenceHT/KeyPeriod.vue'
import KeyMinus from '@/components/svg/keyenceHT/KeyMinus.vue'

const KEY_ICON_MAP: Record<KeyenceHardKey, unknown> = {
  [KeyenceHardKey.F1]: KeyF1,
  [KeyenceHardKey.F2]: KeyF2,
  [KeyenceHardKey.F3]: KeyF3,
  [KeyenceHardKey.F4]: KeyF4,
  [KeyenceHardKey.BACKSPACE]: KeyC,
  [KeyenceHardKey.ENTER]: KeyEnt,
  [KeyenceHardKey.NUM_0]: Key0,
  [KeyenceHardKey.NUM_1]: Key1,
  [KeyenceHardKey.NUM_2]: Key2,
  [KeyenceHardKey.NUM_3]: Key3,
  [KeyenceHardKey.NUM_4]: Key4,
  [KeyenceHardKey.NUM_5]: Key5,
  [KeyenceHardKey.NUM_6]: Key6,
  [KeyenceHardKey.NUM_7]: Key7,
  [KeyenceHardKey.NUM_8]: Key8,
  [KeyenceHardKey.NUM_9]: Key9,
  [KeyenceHardKey.TAB]: KeyTab,
  [KeyenceHardKey.ARROW_UP]: null,
  [KeyenceHardKey.ARROW_DOWN]: null,
  [KeyenceHardKey.ARROW_LEFT]: null,
  [KeyenceHardKey.ARROW_RIGHT]: null,
  [KeyenceHardKey.PERIOD]: KeyPeriod,
  [KeyenceHardKey.MINUS]: KeyMinus,
}

@Options({
  name: 'KeyenceHTHardKeyButton',
  components: {
    Key0,
    Key1,
    Key2,
    Key3,
    Key4,
    Key5,
    Key6,
    Key7,
    Key8,
    Key9,
    KeyC,
    KeyEnt,
    KeyF1,
    KeyF2,
    KeyF3,
    KeyF4,
    KeyTab,
    KeyPeriod,
    KeyMinus,
  },
})
export default class KeyenceHTHardKeyButton extends Vue {
  @Prop({ type: String, required: true })
  hardkey!: KeyenceHardKey

  @Prop({ type: Function, required: true })
  function!: () => void | Promise<void>

  @Prop({ type: Boolean, default: true })
  preventDefault!: boolean

  @Prop({ type: Boolean, default: true })
  stopPropagation!: boolean

  @Prop({ type: Boolean, default: false })
  disabled!: boolean

  @Prop({ type: Boolean, default: true })
  visible!: boolean

  @Prop({ type: Boolean, default: false })
  newLayer!: boolean

  @Prop({ type: String, default: 'overlay' })
  indicatorPlacement!: 'overlay' | 'below'

  private componentId = Symbol('keyence-hard-key-button')
  private isRegistered = false
  private layerPushed = false
  private pushedLayerId: symbol | null = null

  get keyIconComponent(): unknown {
    return KEY_ICON_MAP[this.hardkey] || null
  }

  get shouldShowIndicator(): boolean {
    return this.visible && !this.disabled
  }

  get indicatorStyle(): Record<string, string> {
    if (this.indicatorPlacement === 'below') {
      return {
        position: 'relative',
        marginTop: '4px',
      }
    }

    return {
      position: 'absolute',
      bottom: '0px',
      right: '0px',
      top: 'auto',
      left: 'auto',
      margin: '0',
      alignSelf: 'flex-end',
      marginLeft: 'auto',
    }
  }

  private get handleKeyPress(): HardKeyHandler {
    return () => {
      if (!this.disabled && this.function) {
        this.function()
      }
    }
  }

  private registerKey(): void {
    if (this.isRegistered) {
      return
    }

    if (this.newLayer) {
      this.pushedLayerId = keyenceHardKeyEventManager.pushLayer(this.componentId)
      this.layerPushed = true
    }

    keyenceHardKeyEventManager.register(this.componentId, this.hardkey, this.handleKeyPress, {
      preventDefault: this.preventDefault,
      stopPropagation: this.stopPropagation,
    })

    this.isRegistered = true
    this.syncHandlerEnabledState()
  }

  private syncHandlerEnabledState(): void {
    keyenceHardKeyEventManager.setEnabled(this.componentId, !this.disabled)
  }

  private unregisterKey(): void {
    if (!this.isRegistered) {
      return
    }

    keyenceHardKeyEventManager.unregisterAll(this.componentId)

    if (this.layerPushed) {
      keyenceHardKeyEventManager.popLayer(this.pushedLayerId ?? undefined)
      this.pushedLayerId = null
      this.layerPushed = false
    }

    this.isRegistered = false
  }

  @Watch('visible', { immediate: true })
  onVisibleChange(isVisible: boolean): void {
    if (isVisible) {
      this.registerKey()
    } else {
      this.unregisterKey()
    }
  }

  @Watch('disabled')
  onDisabledChange(): void {
    if (this.isRegistered) {
      this.syncHandlerEnabledState()
    }
  }

  private ensureParentPositioning(): void {
    if (this.indicatorPlacement === 'below') {
      return
    }

    const el = (this as unknown as { $el: HTMLElement }).$el
    let targetParent: HTMLElement | null = el?.parentElement || null

    // Find the button element (might be grandparent)
    while (targetParent && targetParent.tagName !== 'BUTTON') {
      if (
        targetParent.classList.contains('el-button') ||
        targetParent.getAttribute('role') === 'button'
      ) {
        break
      }
      targetParent = targetParent.parentElement
    }

    if (targetParent) {
      const computedStyle = window.getComputedStyle(targetParent)
      if (computedStyle.position === 'static') {
        targetParent.style.position = 'relative'
      }
    }
  }

  mounted(): void {
    this.ensureParentPositioning()
  }

  unmounted(): void {
    this.unregisterKey()
  }
}
